import * as XLSX from 'xlsx';

/**
 * Represents a line from the CMI Excel file
 */
class LigneDolibarr {
    constructor(row = []) {
        if (row.length > 0) {
            this.dateFacture = row[0];
            this.dateTransaction = row[1];
            this.client = row[2];
            this.heure = row[3];
            this.numeroAutorisation = row[4];
            this.systeme = row[5];
            this.numeroCarte = row[6];
            this.stan = row[7];
            this.devise = row[8];
            this.montantBrutTTC = row[9];
            this.totalTTC = row[9]; // Keep for backward compatibility
            this.commission = row[10];
            this.tvaCommission = row[11];
            this.montantNetTTC = row[12];
            this.remiseBanque = row[13];
        }
    }

    /**
     * Validates Excel file headers
     */
    static checkExcel(firstRow) {
        return (
            firstRow[0] === "Date de traitement" &&
            firstRow[2] === "N° PDV" &&
            firstRow[8] === "Devise" &&
            firstRow[9] === "Montant brut TTC" &&
            firstRow[13] === "Numéro de remise"
        );
    }
}

/**
 * Aggregates CMI transactions into factures
 */
class Factures {
    constructor() {
        this.factures = {};
    }

    /**
     * Adds a transaction line to the factures collection
     */
    add(lineDolibarr, company = "oflorist") {
        const key = lineDolibarr.remiseBanque + lineDolibarr.dateFacture.replace(/\//g, "");

        if (this.factures[key]) {
            // Existing facture - add to total and raw transactions
            const ar = this.factures[key];
            ar.lines[0].subprice += lineDolibarr.totalTTC;
            ar.lines[0].trx++;
            ar.rawTransactions.push({
                dateTransaction: lineDolibarr.dateTransaction,
                heure: lineDolibarr.heure,
                numeroCarte: lineDolibarr.numeroCarte,
                systeme: lineDolibarr.systeme,
                montantBrutTTC: lineDolibarr.montantBrutTTC,
                montantNetTTC: lineDolibarr.montantNetTTC,
                commission: lineDolibarr.commission,
                tvaCommission: lineDolibarr.tvaCommission,
                numeroAutorisation: lineDolibarr.numeroAutorisation,
                stan: lineDolibarr.stan,
                devise: lineDolibarr.devise
            });
            this.factures[key] = ar;
        } else {
            // New facture
            const name = this.getTheName(company);
            this.factures[key] = {
                socid: this.getClientId(lineDolibarr.client),
                remiseId: key,
                company: company,
                datef: lineDolibarr.dateFacture,
                note_private: lineDolibarr.remiseBanque,
                rawTransactions: [{
                    dateTransaction: lineDolibarr.dateTransaction,
                    heure: lineDolibarr.heure,
                    numeroCarte: lineDolibarr.numeroCarte,
                    systeme: lineDolibarr.systeme,
                    montantBrutTTC: lineDolibarr.montantBrutTTC,
                    montantNetTTC: lineDolibarr.montantNetTTC,
                    commission: lineDolibarr.commission,
                    tvaCommission: lineDolibarr.tvaCommission,
                    numeroAutorisation: lineDolibarr.numeroAutorisation,
                    stan: lineDolibarr.stan,
                    devise: lineDolibarr.devise
                }],
                lines: [{
                    desc: name,
                    description: name,
                    tva_tx: (company === "oflorist") ? 0 : 20,
                    currency: lineDolibarr.devise,
                    qty: 1,
                    subprice: lineDolibarr.totalTTC,
                    product_type: 'product',
                    trx: 1
                }]
            };
        }
    }

    /**
     * Maps client numbers to Dolibarr IDs
     */
    getClientId(remise) {
        const trimmedRemise = String(remise).trim();
        switch (trimmedRemise) {
            case "7800692530":
                return 31;
            case "9900649014":
                return 67;
            case "9900775868":
                return 79;
            default:
                return null;
        }
    }

    /**
     * Returns all processed factures as an array
     */
    getFactures() {
        return Object.values(this.factures);
    }

    /**
     * Returns a random product name based on company
     */
    getTheName(company = "oflorist") {
        let names = [];
        if (company === "oflorist") {
            names = [
                "FLEUR COUPEE",
                "POT LOCAL",
                "PLANTES DIVERS"
            ];
        } else {
            names = [
                "Vases",
                "Fleurs Séchées",
                "Plante Artificielle",
                "Fleur Artificielle",
                "Cloche",
                "Fleur Stabilisé",
                "Composition séchée",
                "Composition Artificielle"
            ];
        }

        const randomValue = Math.floor(Math.random() * names.length);
        return names[randomValue];
    }
}

/**
 * Process CMI Excel file and return formatted factures
 * @param {File} file - The Excel file to process
 * @param {string} company - The company name (oflorist, etc.)
 * @returns {Promise<Array>} - Array of processed factures
 */
export const processCmiExcelFile = (file, company) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                // Get first worksheet
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];

                // Convert to array of arrays
                const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                // Validate and process
                if (rows.length > 1 && LigneDolibarr.checkExcel(rows[0])) {
                    const factures = new Factures();

                    // Process each row (skip header)
                    for (let i = 1; i < rows.length; i++) {
                        const ligne = new LigneDolibarr(rows[i]);
                        factures.add(ligne, company);
                    }

                    const result = factures.getFactures();
                    resolve(result);
                } else {
                    // Invalid Excel format
                    reject(new Error('Format de fichier Excel invalide. Vérifiez les en-têtes.'));
                }
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = (error) => {
            reject(error);
        };

        reader.readAsArrayBuffer(file);
    });
};
